import React, { ChangeEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import Editor from 'src/components/Editor/Editor';
import Button from 'src/components/Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import { convertHTMLToString } from 'src/helpers/validationHelper';
import {
  Specification,
  SpecificationSavePayload,
} from 'src/SharedCustomHooks/UseSpecifications/useSpecifications';
import './SpecificationForm.scss';

interface SpecificationFormProps {
  itemId: string;
  specification?: Partial<Specification>;
  saving: boolean;
  onSaveSpecification: (payload: SpecificationSavePayload) => Promise<boolean>;
  onClose: () => void;
}

const SpecificationForm = ({
  itemId,
  specification,
  saving,
  onSaveSpecification,
  onClose,
}: SpecificationFormProps) => {
  const user: any = useSelector((state: any) => state.auth?.user || {});

  const [title, setTitle] = useState<string>(specification?.title || '');
  const [description, setDescription] = useState<string>(
    specification?.description || ''
  );

  const isEditMode: boolean = !!specification?.id;

  const descriptionText: string =
    convertHTMLToString(description)?.trim() || '';

  const isValid: boolean = !!title.trim() && !!descriptionText;

  const clearForm = (): void => {
    setTitle('');
    setDescription('');
  };

  const buildPayload = (): SpecificationSavePayload => {
    const base: SpecificationSavePayload = {
      title: title.trim(),
      description,
      catalogId: itemId,
      userId: user.id,
    };

    return specification?.id ? { ...base, id: specification.id } : base;
  };

  const handleSave = async (
    type: 'SaveAndClose' | 'SaveAndAdd'
  ): Promise<void> => {
    const success: boolean = await onSaveSpecification(buildPayload());

    if (!success) {
      return;
    }

    if (type === 'SaveAndClose') {
      onClose();
    } else {
      clearForm();
    }
  };

  return (
    <div className="specification-form">
      <p className="specification-form-label">Title</p>
      <input
        className="specification-form-title"
        type="text"
        placeholder="Specification title"
        value={title}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setTitle(e.target.value)
        }
      />

      <p className="specification-form-label">Description</p>
      <Editor
        editorState={description}
        onEditorStateChange={setDescription}
        placeholder="Describe this specification..."
        minHeight={160}
        maxHeight={260}
      />

      <div className="specification-form-action-row">
        {!isEditMode && (
          <Button
            color="semantic"
            size="small"
            label="Save and Add Another"
            onClick={() => handleSave('SaveAndAdd')}
            disabled={!isValid || saving}
          />
        )}
        <LoadingButton
          actionText={isEditMode ? 'Save Changes' : 'Save Specification'}
          onClick={() => handleSave('SaveAndClose')}
          disabled={!isValid}
          isSubmitting={saving}
        />
      </div>
    </div>
  );
};

export default SpecificationForm;
